'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@workspace/ui/components/avatar';
import { Button } from '@workspace/ui/components/button';
import { Icon } from './Icon';
import { useEffect } from 'react';
import { getInvoice } from '@/src/actions/stripe';
import getDonationInvoice from '@/src/utils/getDonationInvoice';
import { cn } from '@workspace/ui/lib/utils';

type Props = {
  title?: string;
  description?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  creatorAvatarUrl?: string;
  isBillable: boolean;
  subscriptionId?: string;
  creatorStripeId: string;
  donationId?: string;
};

export const InfoDialog = ({
  title,
  description,
  isOpen = false,
  onOpenChange,
  onClose,
  creatorAvatarUrl,
  subscriptionId,
  isBillable = false,
  creatorStripeId,
  donationId,
}: Props) => {
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange?.(false);
        onClose?.();
      }
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onOpenChange, onClose]);

  if (!isOpen) return null;
  const handleViewInvoice = async () => {
    if (subscriptionId && !donationId) {
      const invoice = await getInvoice(subscriptionId, creatorStripeId);
      if (invoice) {
        window.open(invoice, '_blank');
      }
    } else if (donationId) {
      const url = await getDonationInvoice(donationId);
      window.open(url as string, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center h-screen w-screen bg-black/30 backdrop-blur-sm">
      <div className="bg-white p-4 rounded-2xl shadow-md md:w-[540px] w-95% flex flex-col items-center gap-2">
        {donationId && !subscriptionId && (
          <Avatar className="md:size-14 size-5 border-4 border-transparent bg-transparent">
            <AvatarImage
              src={creatorAvatarUrl || '/images/default-avatar.png'}
              alt="profile image"
            />
          </Avatar>
        )}
        <h3 className="text-2xl font-semibold">{title}</h3>
        <p className="text-black/60 text-base font-light text-center">
          {description}
        </p>
        <hr className="w-full border-gray-100 my-4" />
        <div
          className={cn('w-full flex justify-center', {
            'justify-between': isBillable,
          })}
        >
          {isBillable && (
            <Button
              size="sm"
              variant="outline"
              className="bg-transparent px-4"
              onClick={handleViewInvoice}
            >
              View the bill
            </Button>
          )}
          <Button
            size="sm"
            className="px-4"
            onClick={() => {
              onOpenChange?.(false);
              onClose?.();
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
